import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { usePgType, usePlusQueryParameters } from "scmp-app/components/content/helpers";
import { PlusFlag } from "scmp-app/components/plus/plus-flag";
import type { secondaryContentItemContent$key } from "scmp-app/queries/__generated__/secondaryContentItemContent.graphql";

import {
  Container,
  Content,
  Cover,
  Footer,
  Header,
  LiveTagContainer,
  StyledCoverEntityLink,
  StyledHeadlineEntityLink,
  TopicLinkContainer,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: secondaryContentItemContent$key;
};

export const ContentItemSecondary = forwardRef<HTMLDivElement, Props>(
  ({ className, reference: reference_, topicLinkVariant }, reference) => {
    const entity = useFragment(
      graphql`
        fragment secondaryContentItemContent on Content {
          __typename
          ...entityLink
          ...helpersCheckIsPlusArticle
          ...helpersUseContentSponsorContent
          ...hooksContentItemProvidersContent
            @arguments(
              withLiveTag: true
              withTopicLink: true
              withHeadline: true
              withCoverImage: true
              withCoverImageSize540x360: true
              withCoverImageSize80x80: true
              withVideoDurationOverlay: true
              withSummary: true
              withActionBar: true
              withSponsorTag: true
            )
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(entity, topicLinkVariant);
    const isPlusArticle = checkIsPlusArticle(entity);

    const pgtype = usePgType();
    const plusQueryParameters = usePlusQueryParameters(entity);

    const header = (
      <>
        <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>
        {isPlusArticle && <PlusFlag pgtype={pgtype} />}
        <TopicLinkContainer>{providers.topicLink?.()}</TopicLinkContainer>
        <StyledHeadlineEntityLink query={plusQueryParameters} reference={entity}>
          {providers.headline({
            hideTooltip: true,
            preferSocialHeadline: true,
          })}
        </StyledHeadlineEntityLink>
      </>
    );
    const cover = (
      <StyledCoverEntityLink query={plusQueryParameters} reference={entity}>
        {providers.coverImage({
          responsiveVariants: {
            desktopUp: "size540x360",
            mobileUp: "size80x80",
            tabletUp: "size540x360",
          },
        })}
        {providers.video?.durationOverlay()}
      </StyledCoverEntityLink>
    );
    const contentSponsor = getContentSponsor(entity);
    const isSponsorArticle = notEmpty(contentSponsor?.name);
    const content = isSponsorArticle ? null : providers.summary();
    const footer = isSponsorArticle
      ? providers.sponsorTag?.({
          color: "#001246",
          gap: "0px",
          layoutDirection: "vertical",
          nameFontSize: "12px",
          typeFontSize: "12px",
        })
      : providers.actionBar();

    return (
      <Container className={className} ref={reference}>
        <Header>{header}</Header>
        <Cover>{cover}</Cover>
        <Content>{content}</Content>
        <Footer>{footer}</Footer>
      </Container>
    );
  },
);

ContentItemSecondary.displayName = "ContentItemSecondary";
